import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { Droplets, LogOut } from "lucide-react";
import { api, ApiError } from "../api";
import { useAuth } from "./AuthProvider";
import { resolveBusiness, resolveSite, type Membership, type SiteOption } from "./scope";
import { setScope } from "./session";
import { setScopeTags } from "../sentry";
import { OnboardingWizard, type OnboardingBusiness } from "../onboarding/OnboardingWizard";

export type ResolvedScope = {
  businessId: string; businessName: string; siteId: string; siteName: string; role: string;
  // Whether the business can send customer-facing SMS yet — false until the maintainer
  // activates it out of band. Drives the pending-activation banner on the board.
  activated: boolean;
};

const ScopeContext = createContext<ResolvedScope | null>(null);

export function useScope(): ResolvedScope {
  const ctx = useContext(ScopeContext);
  if (!ctx) throw new Error("useScope must be used inside ScopeGate");
  return ctx;
}

// Remembering the pick per uid, so an ordinary reload doesn't re-prompt an owner who works
// at one site all day — and so the NEXT person to sign in on the same tablet gets their own
// remembered pick, never the previous user's. Same per-uid discipline as the persisted cache.
const selectionKey = (uid: string) => `carwash-staff-scope:${uid}`;

type Selection = { businessId: string | null; siteId: string | null };

function readSelection(uid: string): Selection {
  try {
    const raw = localStorage.getItem(selectionKey(uid));
    const parsed = raw ? (JSON.parse(raw) as Partial<Selection>) : {};
    return { businessId: parsed.businessId ?? null, siteId: parsed.siteId ?? null };
  } catch {
    return { businessId: null, siteId: null }; // unreadable storage → just ask again
  }
}

function writeSelection(uid: string, selection: Selection): void {
  try {
    localStorage.setItem(selectionKey(uid), JSON.stringify(selection));
  } catch {
    /* private mode / quota — the picker simply reappears next time */
  }
}

type State =
  | { phase: "loading" }
  | { phase: "failed"; message: string }
  // A signed-in person genuinely has nowhere to go: a SiteManager whose RoleAssignment
  // names no site. Unlike "onboarding" below, self-serve cannot fix this — only an owner
  // reassigning them can.
  | { phase: "noAccess" }
  // Self-serve setup still in progress. `business` is null before POST /businesses has run
  // for this session (zero memberships); once it exists but has no sites yet, `business`
  // carries it so the wizard resumes at the site step instead of restarting. `hasSite` /
  // `hasServices` are only meaningful once `business` is non-null — they are what lets the
  // wizard pick SiteStep vs ServiceStep for the same non-null business, in that priority order
  // (a site is required before a service can be added from the wizard).
  | { phase: "onboarding"; business: OnboardingBusiness | null; hasSite: boolean; hasServices: boolean }
  | { phase: "pickBusiness"; options: Membership[] }
  | { phase: "pickSite"; membership: Membership; options: SiteOption[] }
  | { phase: "ready"; scope: ResolvedScope };

// Turns "who is this signed-in person?" into "which business and site is this session
// scoped to?", replacing the build-time VITE_BUSINESS_ID / VITE_SITE_ID this app used to
// require. The decisions themselves are the pure functions in scope.ts; this component only
// fetches, renders the pickers, and publishes the result.
export function ScopeGate({ uid, children }: { uid: string; children: (scope: ResolvedScope) => ReactNode }) {
  const auth = useAuth();
  const [state, setState] = useState<State>({ phase: "loading" });
  const [selection, setSelection] = useState<Selection>(() => readSelection(uid));
  // Bumped by "Try again": the resolve effect is keyed on it, so a retry actually re-runs.
  const [attempt, setAttempt] = useState(0);
  // A failed business-create attempt, stashed OUTSIDE `state` on purpose: onCreated() below
  // drives state through loading→onboarding in the same tick that the wizard's catch handler
  // stashes this, and if it lived inside `state` that round-trip would overwrite it before the
  // remount ever saw it. Cleared as soon as the resolve moves past "onboarding with no
  // business yet" — a fresh mount for a new session must never inherit stale text.
  // clientCommandId rides along for the same reason the name does, and is the load-bearing part:
  // a retry that mints a fresh id reads as a genuine second business at the server, so the
  // replay guard on POST /businesses would never fire. Carrying the ORIGINAL id across the
  // failure→remount→resubmit round-trip is what makes the retry idempotent.
  const [onboardingDraft, setOnboardingDraft] =
    useState<{ error: string; name: string; clientCommandId: string } | null>(null);

  const choose = useCallback((next: Selection) => {
    writeSelection(uid, next);
    setSelection(next);
    setState({ phase: "loading" });
  }, [uid]);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      try {
        const me = await api.staffMe();
        if (cancelled) return;
        const business = resolveBusiness(me.memberships, selection.businessId);

        if (business.kind === "none") {
          setState({ phase: "onboarding", business: null, hasSite: false, hasServices: false });
          return;
        }
        if (business.kind === "pick") {
          setState({ phase: "pickBusiness", options: business.options });
          return;
        }

        const membership = business.membership;

        // A site manager must NOT list the business's sites: GET /businesses/{id}/sites is a
        // business-wide route, so BusinessAccessMiddleware answers 403 for them. Their site
        // comes from their own membership instead — no request, no picker.
        const sites: SiteOption[] = membership.role === "SiteManager"
          ? []
          : (await api.listSites(membership.businessId)).map((s) => ({ id: s.id, name: s.name }));

        const site = resolveSite(membership, sites, selection.siteId);
        if (cancelled) return;

        if (site.kind === "none") {
          // hasServices is moot here: SiteStep takes priority over ServiceStep regardless of
          // catalog state, so there is no reason to spend a round-trip on it yet — it's fetched
          // once (and only once) the site question is actually settled, below.
          setState(membership.role === "SiteManager"
            ? { phase: "noAccess" }
            : {
              phase: "onboarding",
              business: { id: membership.businessId, name: membership.businessName },
              hasSite: false,
              hasServices: false,
            });
          return;
        }
        if (site.kind === "pick") {
          setState({ phase: "pickSite", membership, options: site.options });
          return;
        }

        // The site is resolved — a service is the only thing that can still be missing, so
        // this is the only branch that needs the catalog at all. A SiteManager can't self-serve
        // a missing service any more than a missing site, so they skip the request too.
        //
        // Deliberately "any item exists", NOT "any ACTIVE item exists". The wizard is a one-way
        // door: its service step has no header, no Settings link and no way back to the board, so
        // keying it on the ACTIVE count would trap an owner who deactivates their only service in
        // /settings (to reprice it, or because they're closed) the moment the tablet reloads —
        // load shedding, a PWA update, a closed tab. A business that has ever defined a service has
        // finished onboarding; whether it currently trades is the board's concern, and
        // WalkInModal's empty state already covers "no active services" properly.
        const hasServices = membership.role === "SiteManager"
          ? true
          : (await api.listCatalogItems(membership.businessId)).length > 0;
        if (cancelled) return;

        // Onboarding is complete only once a service exists too — a reload with a business and a
        // site but no service must resume at the wizard's service step, not the board.
        if (!hasServices) {
          setState({
            phase: "onboarding",
            business: { id: membership.businessId, name: membership.businessName },
            hasSite: true,
            hasServices: false,
          });
          return;
        }

        setState({
          phase: "ready",
          scope: {
            businessId: membership.businessId,
            businessName: membership.businessName,
            siteId: site.siteId,
            siteName: sites.find((s) => s.id === site.siteId)?.name ?? "",
            role: membership.role,
            activated: membership.activated,
          },
        });
      } catch (e) {
        if (cancelled) return;
        // A 401 has already triggered sign-out inside api.send; anything else is a real
        // failure worth showing with a retry rather than a blank screen.
        setState({
          phase: "failed",
          message: e instanceof ApiError ? e.detail : "Couldn't reach the server.",
        });
      }
    };

    void resolve();
    return () => { cancelled = true; };
  }, [uid, selection, attempt]);

  // The stashed draft is only ever relevant while displaying the business-name form; discard
  // it the moment the resolve finds a business (site step or beyond) so it can't leak into a
  // later, unrelated onboarding attempt.
  useEffect(() => {
    // "loading" is skipped deliberately: onCreated() always passes through it on the way
    // back to "onboarding" (business still null, for a genuine failure), and clearing here
    // during that transient step would erase the draft before the remount ever reads it.
    if (state.phase === "loading") return;
    if (onboardingDraft && (state.phase !== "onboarding" || state.business)) {
      setOnboardingDraft(null);
    }
  }, [state, onboardingDraft]);

  // Publish the ids api.ts reads for its tenant-scoped URLs — DURING RENDER, before
  // children(...) below is called.
  //
  // Writing a module value while rendering is normally forbidden; it is correct here for two
  // specific reasons. First, the module holder IS the render-visible state the child tree
  // reads: QueueBoard → useQueueSignalR → requireScope(), and visitsApi → visitsKey(
  // requireScope()), both during their own render. React renders children before running this
  // component's passive effects, so publishing from a useEffect leaves the holder null on the
  // first "ready" render and requireScope() throws — a white screen, since staff-web has no
  // error boundary. Second, the write is idempotent: the same state always produces the same
  // ids, so a re-render (or StrictMode's double render) changes nothing.
  const businessId = state.phase === "ready" ? state.scope.businessId : null;
  const siteId = state.phase === "ready" ? state.scope.siteId : null;
  setScope(businessId && siteId ? { businessId, siteId } : null);

  // Sentry's business/site filter, ids only (POPIA). A genuine side effect, so it stays in an
  // effect — only the scope holder has to be render-synchronous.
  useEffect(() => {
    setScopeTags(businessId && siteId ? { businessId, siteId } : null);
  }, [businessId, siteId]);

  // Clear on unmount so a sign-out can never leave the previous session's business
  // addressable. Leaving the ready phase is already covered by the render-time publish above,
  // which writes null for every non-ready phase — and this cleanup deliberately does NOT run
  // on a scope change, which would otherwise null out the value just published during render.
  useEffect(() => () => { setScope(null); setScopeTags(null); }, []);

  if (state.phase === "ready") {
    return <ScopeContext.Provider value={state.scope}>{children(state.scope)}</ScopeContext.Provider>;
  }

  if (state.phase === "loading") return <Centered>Loading your sites…</Centered>;

  if (state.phase === "failed") {
    return (
      <Centered onSignOut={auth.signOut}>
        <p className="text-slate-600">{state.message}</p>
        <button
          onClick={() => { setState({ phase: "loading" }); setAttempt((n) => n + 1); }}
          className={primaryButton}
        >
          Try again
        </button>
      </Centered>
    );
  }

  if (state.phase === "noAccess") {
    return (
      <Centered onSignOut={auth.signOut}>
        <p className="text-slate-600">
          You're signed in, but you don't have access to a carwash yet. Ask an owner to send you an invite.
        </p>
      </Centered>
    );
  }

  if (state.phase === "onboarding") {
    return (
      <OnboardingWizard
        business={state.business}
        hasSite={state.hasSite}
        hasServices={state.hasServices}
        // Re-run the resolve effect: the NEXT fetch of /auth/staff/me is what actually
        // advances the wizard (and is what makes a reload resume at the right step — the
        // wizard itself never tracks "which step" locally).
        onCreated={() => { setState({ phase: "loading" }); setAttempt((n) => n + 1); }}
        onSignOut={auth.signOut}
        initialError={onboardingDraft?.error}
        initialName={onboardingDraft?.name}
        initialClientCommandId={onboardingDraft?.clientCommandId}
        onFailedCreate={(error, name, clientCommandId) => setOnboardingDraft({ error, name, clientCommandId })}
      />
    );
  }

  if (state.phase === "pickBusiness") {
    return (
      <Centered onSignOut={auth.signOut}>
        <h2 className="font-semibold text-slate-800">Choose a carwash</h2>
        {state.options.map((m) => (
          <button
            key={m.businessId} className={primaryButton}
            onClick={() => choose({ businessId: m.businessId, siteId: null })}
          >
            {m.businessName}
          </button>
        ))}
      </Centered>
    );
  }

  return (
    <Centered onSignOut={auth.signOut}>
      <h2 className="font-semibold text-slate-800">Choose a site</h2>
      {state.options.map((s) => (
        <button
          key={s.id} className={primaryButton}
          onClick={() => choose({ businessId: state.membership.businessId, siteId: s.id })}
        >
          {s.name}
        </button>
      ))}
    </Centered>
  );
}

const primaryButton =
  "w-full rounded-xl px-4 py-2.5 font-semibold text-white shadow bg-[#0fa3b1] active:scale-95 transition";

function Centered({ children, onSignOut }: { children: ReactNode; onSignOut?: () => Promise<void> }) {
  return (
    <div className="min-h-screen grid place-items-center p-6" style={{ background: "#eef2f4" }}>
      <div className="w-full max-w-sm space-y-3 rounded-2xl bg-white p-6 text-center shadow-lg">
        <div className="mx-auto w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ background: "#0fa3b1" }}>
          <Droplets size={20} />
        </div>
        {children}
        {onSignOut && (
          <button onClick={() => void onSignOut()} className="mx-auto flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
            <LogOut size={16} /> Sign out
          </button>
        )}
      </div>
    </div>
  );
}
